import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

const SidebarContainer = styled.aside`
  width: 260px;
  min-height: 100vh;
  background: #1a1f2e;
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;

  @media (max-width: 768px) {
    width: 100%;
    min-height: auto;
    position: relative;
  }
`;

const Logo = styled.div`
  padding: 24px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #2DBCAF 0%, #18938a 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const LogoText = styled.div`
  color: #fff;
  font-weight: 700;
  font-size: 18px;
  
  span {
    color: #2DBCAF;
  }
`;

const Nav = styled.nav`
  flex: 1;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  color: #8b95a5;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }

  &.active {
    background: rgba(45, 188, 175, 0.15);
    color: #2DBCAF;
  }

  .material-symbols-outlined {
    font-size: 20px;
  }
`;

const UserSection = styled.div`
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  color: #2DBCAF;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
`;

const LogoutBtn = styled.button`
  background: transparent;
  border: none;
  color: #8b95a5;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #ef4444;
  }

  .material-symbols-outlined {
    font-size: 20px;
  }
`;

const BackToSite = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin: 8px 12px;
  border-radius: 10px;
  color: #8b95a5;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  border: 1px dashed rgba(255, 255, 255, 0.15);
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }

  .material-symbols-outlined {
    font-size: 18px;
  }
`;

const menuItems = [
  { path: '/admin', icon: 'dashboard', label: 'Dashboard', end: true },
  { path: '/admin/users', icon: 'group', label: 'Pengguna' },
  { path: '/admin/payments', icon: 'payments', label: 'Pembayaran' },
  { path: '/admin/games', icon: 'sports_esports', label: 'Games' },
  { path: '/admin/dongeng', icon: 'auto_stories', label: 'Dongeng' },
  { path: '/admin/tumbuh-kembang', icon: 'child_care', label: 'Tumbuh Kembang' },
  { path: '/admin/email', icon: 'mail', label: 'Email Marketing' },
  { path: '/admin/vouchers', icon: 'confirmation_number', label: 'Voucher' },
  { path: '/admin/settings', icon: 'settings', label: 'Pengaturan' },
];

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <SidebarContainer>
      <Logo>
        <LogoIcon>🎮</LogoIcon>
        <LogoText>Kinder<span>game</span></LogoText>
      </Logo>

      <Nav>
        {menuItems.map((item) => (
          <NavItem key={item.path} to={item.path} end={item.end}>
            <span className="material-symbols-outlined">{item.icon}</span>
            {item.label}
          </NavItem>
        ))}
      </Nav>

      <BackToSite href="/" target="_blank">
        <span className="material-symbols-outlined">open_in_new</span>
        Lihat Website
      </BackToSite>

      <UserSection>
        <Avatar 
          src={user?.avatar || '/assets/images/default-avatar.png'} 
          alt={user?.name || 'User'} 
        />
        <UserInfo>
          <UserName>{user?.name || 'Admin'}</UserName>
          <UserRole>{user?.role || 'ADMIN'}</UserRole>
        </UserInfo>
        <LogoutBtn onClick={handleLogout} title="Logout">
          <span className="material-symbols-outlined">logout</span>
        </LogoutBtn>
      </UserSection>
    </SidebarContainer>
  );
};

export default Sidebar;
